import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilValue } from 'recoil';
import {
	Alert as ReactstrapAlert,
	AlertProps as ReactstrapAlertProps,
	UncontrolledAlert,
	UncontrolledAlertProps as ReactstrapUncontrolledAlertProps
} from 'reactstrap';

import { isAlertsContainerMountedState } from '../../atoms';
import { ALERTS_CONTAINER_ID } from '../../config';

type CommonProps = {
	fallbackElement?: React.MutableRefObject<Element>;
	screenMessage?: string;
	children?: JSX.Element[] | JSX.Element
};

type AlertsContainerPortalProps = CommonProps & {
	element: Element | null;
};

const AlertsContainerPortal: React.FC<AlertsContainerPortalProps > = ({
	children,
	element,
	fallbackElement
}) => {
	if (element !== null) return createPortal(children, element);

	if (fallbackElement && fallbackElement.current)
		return createPortal(children, fallbackElement.current);

	return null;
};

export type ControlledAlertProps = CommonProps &
	ReactstrapAlertProps & {
		type: 'controlled';
	};

export type UncontrolledAlertProps = CommonProps &
	ReactstrapUncontrolledAlertProps & {
		type: 'uncontrolled';
	};

export type AlertProps = ControlledAlertProps | UncontrolledAlertProps;

export const Alert: React.FC<AlertProps> = ({
	children,
	fallbackElement,
	className,
	type,
	screenMessage,
	...props
}) => {
	className = `shadow-lg${className ? ` ${className}` : ''}`;

	const isAlertsContainerMounted = useRecoilValue(
		isAlertsContainerMountedState
	);

	const alertsContainerElement = useMemo(
		() =>
			isAlertsContainerMounted
				? document.getElementById(ALERTS_CONTAINER_ID)
				: null,
		[isAlertsContainerMounted]
	);

	let body: JSX.Element;

	switch (type) {
		case 'controlled':
			body = (
				<ReactstrapAlert className={className} {...props}>
					{children}
				</ReactstrapAlert>
			);

			break;
		case 'uncontrolled':
			body = (
				<UncontrolledAlert className={className} {...props}>
					{children}
				</UncontrolledAlert>
			);

			break;
	}

	return (
		<AlertsContainerPortal
			element={alertsContainerElement}
			fallbackElement={fallbackElement}
		>
			{body}
		</AlertsContainerPortal>
	);
};
